// G5 (Phase 1 quotation): an approved consultation note is never edited.
// A correction is saved as the next version with a reason, and the earlier
// version is kept and marked superseded (backend: utils/consultationAmendment.js).
// Used by Patient Records → History and the Consultation screen's
// "Previous consultations" card.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { LoadingState } from '@/components/ui/States';
import DocumentEditor from '@/features/ai-assist/DocumentEditor';
import { renderLiteMarkdown } from '@/lib/markdownLite';
import { amendConsultation, getConsultationVersions } from '../../api';

/** The fields of a consultation row these components read. */
export interface AmendableNote {
  id: number;
  doc_type: string;
  final_doc?: string | null;
  approved?: boolean;
  approved_by?: string | null;
  approved_at?: string | null;
  approved_by_user_id?: number | null;
  version?: number | null;
  amends_consultation_id?: number | null;
  superseded_by_consultation_id?: number | null;
  amendment_reason?: string | null;
}

interface NoteVersion extends AmendableNote { document_id?: number | null }

/** Only Consultation Notes are amended here (prescriptions and discharge summaries at their source). */
export const AMENDABLE_DOC_TYPES = ['Consultation Notes'];

/** Mirrors the server's rule; the server re-checks. */
export function canAmendNote(note: AmendableNote, user: { id?: number; role?: string } | null | undefined): boolean {
  if (!note.approved || note.superseded_by_consultation_id || !AMENDABLE_DOC_TYPES.includes(note.doc_type)) return false;
  return user?.role === 'Admin' || (note.approved_by_user_id != null && note.approved_by_user_id === user?.id);
}

const when = (v?: string | null) => (v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

export function VersionTag({ note }: { note: AmendableNote }) {
  if (!note.version || note.version < 2) return null;
  return <Badge variant="info">Amended · version {note.version}</Badge>;
}

export function AmendNoteDialog({ note, onClose, onAmended }: {
  note: AmendableNote | null;
  onClose: () => void;
  onAmended: (amended: AmendableNote) => void;
}) {
  return (
    <Dialog open={!!note} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        {/* Keyed per note: the editor takes its text when it is created. */}
        {note && <AmendNoteForm key={note.id} note={note} onClose={onClose} onAmended={onAmended} />}
      </DialogContent>
    </Dialog>
  );
}

function AmendNoteForm({ note, onClose, onAmended }: {
  note: AmendableNote;
  onClose: () => void;
  onAmended: (amended: AmendableNote) => void;
}) {
  const [text, setText] = useState(note.final_doc ?? '');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!reason.trim()) { setError('Say why the note is being amended.'); return; }
    if (text.trim() === (note.final_doc ?? '').trim()) { setError('Nothing has changed yet. Edit the note, then save.'); return; }
    setSaving(true); setError(null);
    try {
      const amended = await amendConsultation(note.id, text, reason.trim());
      onAmended(amended as AmendableNote);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setSaving(false); }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Amend {note.doc_type}</DialogTitle>
        <DialogDescription>
          The approved note stays in the record unchanged. Your corrected text is saved as version {(note.version ?? 1) + 1},
          with your name, the time and the reason.
        </DialogDescription>
      </DialogHeader>
      <DocumentEditor value={text} onChange={setText} ariaLabel="Amended note" className="min-h-[220px]" />
      <FormField label="Reason for the amendment" className="mt-3">
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={2000}
          placeholder="e.g. Duration of symptoms corrected after the patient's daughter clarified" />
      </FormField>
      {error && <div className="mt-2 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}
      <DialogFooter className="mt-3">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving}>Save amendment</Button>
      </DialogFooter>
    </>
  );
}

export function NoteVersionsDialog({ noteId, onClose }: { noteId: number | null; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['consultation-versions', noteId],
    queryFn: () => getConsultationVersions(noteId as number) as Promise<NoteVersion[]>,
    enabled: noteId != null,
  });
  const versions = [...(q.data ?? [])].reverse();
  return (
    <Dialog open={noteId != null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Version history</DialogTitle>
          <DialogDescription>Every approved version is kept. The newest is the one in the record.</DialogDescription>
        </DialogHeader>
        {q.isLoading ? <LoadingState /> : (
          <ol className="flex list-none flex-col gap-3 p-0">
            {versions.map((v) => (
              <li key={v.id} className="rounded-[var(--radius)] border border-line p-3">
                <div className="mb-1.5 flex flex-wrap items-center gap-2 text-[12px]">
                  <span className="font-semibold">Version {v.version ?? 1}</span>
                  {!v.superseded_by_consultation_id ? <Badge variant="success">Current</Badge> : <Badge variant="neutral">Superseded</Badge>}
                  <span className="text-ink-muted">{v.amends_consultation_id ? 'Amended' : 'Approved'} by {v.approved_by} · {when(v.approved_at)}</span>
                </div>
                {v.amendment_reason && <div className="mb-1.5 text-[12px]"><span className="text-ink-muted">Reason:</span> {v.amendment_reason}</div>}
                <div className="rounded-[var(--radius)] bg-bg p-2.5 text-[12.5px] leading-relaxed text-ink">{renderLiteMarkdown(v.final_doc || '')}</div>
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}
